// PropsPanel.tsx — Right-side quick-edit panel with bidirectional entity selection
// Shows panel properties + expandable per-slot entity rows for all entity groups.
// Clicking a slot row selects that entity in the 3D viewport; selecting in 3D
// highlights the corresponding row here. Full editing via dialog buttons.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useShallowStore } from '../store/useStore';
import type { MpVar, ActiveDialog } from '../types/panelvar';
import type { Slot } from '../types/panelvar';
import FeetInchesInput from './FeetInchesInput';
import type { FeetInchesHandle } from './FeetInchesInput';
import { formatImperial, formatMetric } from '../utils/imperial';
import { calcMaterials } from '../utils/materialsCalc';
import { parseAngleInput, autoAngle } from '../utils/panelGeometry';
import { getStructuralWarnings } from '../utils/structuralWarnings';

// ── Entity group definitions ─────────────────────────────────────────────────
interface EntityGroup {
  key:    string;          // panelvar key on PanelData e.g. 'rovar'
  label:  string;          // Display name e.g. 'Rough Openings'
  dialog: ActiveDialog;    // Dialog to open on header click
  markFn: (data: Record<string, unknown>, i: number) => string;  // Slot label
}

interface EntityCategory {
  label: string;
  groups: EntityGroup[];
}

const ENTITY_CATEGORIES: EntityCategory[] = [
  {
    label: 'Openings',
    groups: [
      { key: 'rovar', label: 'Rough Openings',    dialog: 'rough-openings',   markFn: (d, i) => String(d.mark || `RO-${i + 1}`) },
      { key: 'wdvar', label: 'Windows',           dialog: 'windows',          markFn: (d, i) => String(d.mark || `WD-${i + 1}`) },
      { key: 'drvar', label: 'Man Doors',         dialog: 'man-doors',        markFn: (d, i) => String(d.mark || `DR-${i + 1}`) },
      { key: 'dlvar', label: 'Dock Levelers',     dialog: 'dock-levelers',    markFn: (d, i) => String(d.mark || `DL-${i + 1}`) },
      { key: 'sbvar', label: 'Rect Blockouts',    dialog: 'rect-blockouts',   markFn: (d, i) => String(d.mark || `SB-${i + 1}`) },
      { key: 'rbvar', label: 'Round Blockouts',   dialog: 'round-blockouts',  markFn: (d, i) => String(d.mark || `RB-${i + 1}`) },
    ],
  },
  {
    label: 'Features',
    groups: [
      { key: 'fhvar', label: 'Horiz Features',    dialog: 'horiz-features',   markFn: (d, i) => String(d.mark || `FH-${i + 1}`) },
      { key: 'fvvar', label: 'Vert Features',     dialog: 'vert-features',    markFn: (d, i) => String(d.mark || `FV-${i + 1}`) },
    ],
  },
  {
    label: 'Structure',
    groups: [
      { key: 'plvar', label: 'Pilasters',         dialog: 'pilasters',        markFn: (d, i) => String(d.mark || `PL-${i + 1}`) },
      { key: 'llvar', label: 'Lintels',             dialog: 'lintels',          markFn: (d, i) => String(d.mark || `LL-${i + 1}`) },
      { key: 'tsvar', label: 'Top Steps',          dialog: 'top-profile' as ActiveDialog, markFn: (d, i) => String(d.mark || `TS-${i + 1}`) },
      { key: 'fsvar', label: 'Footing Steps',      dialog: 'footing-shapes' as ActiveDialog, markFn: (d, i) => String(d.mark || `FS-${i + 1}`) },
      { key: 'ssvar', label: 'Side Slabs',          dialog: 'side-slabs' as ActiveDialog, markFn: (d, i) => String(d.mark || `SS-${i + 1}`) },
    ],
  },
  {
    label: 'Connections',
    groups: [
      { key: 'bpvar', label: 'Brace Points',      dialog: 'brace-points',     markFn: (d, i) => String(d.mark || `BP-${i + 1}`) },
      { key: 'ppvar', label: 'Pick Points',       dialog: 'pick-points',      markFn: (d, i) => String(d.mark || `PP-${i + 1}`) },
      { key: 'wcvar', label: 'Weld Connections',  dialog: 'weld-connections', markFn: (d, i) => String(d.mark || `WC-${i + 1}`) },
      { key: 'sdvar', label: 'Slab Dowels',       dialog: 'slope-details' as ActiveDialog, markFn: (d, i) => String(d.mark || `SD-${i + 1}`) },
    ],
  },
];

export default function PropsPanel({ style }: { style?: React.CSSProperties }) {
  const { ui, project, updateMpVar, updatePanel, setActiveDialog, toggleProps, toggleUnits, selectEntity, setFocusField } = useShallowStore(s => ({
    ui:              s.ui,
    project:         s.project,
    updateMpVar:     s.updateMpVar,
    updatePanel:     s.updatePanel,
    setActiveDialog: s.setActiveDialog,
    toggleProps:     s.toggleProps,
    toggleUnits:     s.toggleUnits,
    selectEntity:    s.selectEntity,
    setFocusField:   s.setFocusField,
  }));

  const panel = project?.panels.find(p => p.id === ui.activePanelId);

  // Structural warnings for current panel
  const warnings = useMemo(
    () => (panel && project) ? getStructuralWarnings(panel, project.settings) : [],
    [panel, project?.settings],  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const geomWarnIds = useMemo(
    () => new Set(warnings.filter(w => w.category === 'geometry').map(w => w.id)),
    [warnings],
  );

  // Track which entity sections are expanded
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['geometry']));

  // Refs for dim fields (click-to-focus from 2D canvas)
  const dimRefs = useRef<Record<string, FeetInchesHandle | null>>({});

  // Focus listener: when canvas sets focusField, focus the matching input
  useEffect(() => {
    const field = ui.focusField;
    if (!field) return;
    const handle = dimRefs.current[field];
    if (handle) handle.focus();
    setFocusField(null);
  }, [ui.focusField, setFocusField]);

  const selSet = useMemo(() => new Set(ui.selectedEntities), [ui.selectedEntities]);

  // Auto-expand sections containing selected entities
  useEffect(() => {
    if (ui.selectedEntities.length === 0) return;
    const toExpand = new Set<string>();
    for (const eid of ui.selectedEntities) {
      const dash = eid.indexOf('-');
      if (dash > 0) toExpand.add(eid.slice(0, dash));
    }
    if (toExpand.size > 0) {
      setExpanded(prev => {
        const next = new Set(prev);
        for (const k of toExpand) next.add(k);
        return next.size === prev.size ? prev : next;
      });
    }
  }, [ui.selectedEntities]);

  if (!panel) {
    return (
      <aside className={`props-panel${ui.propsOpen ? '' : ' collapsed'}`} style={style}>
        {ui.propsOpen ? (
          <div className="props-empty">No panel selected</div>
        ) : (
          <button className="props-expand-btn" onClick={toggleProps} title="Show Properties">&#x25C4;</button>
        )}
      </aside>
    );
  }

  const mp = panel.mpvar;
  const tp = panel.tpvar;
  const tpThick = tp.thickness;

  function set<K extends keyof MpVar>(key: K, value: MpVar[K]) {
    updateMpVar(panel!.id, { [key]: value } as Partial<MpVar>);
  }

  // Linked height/elevation: height(in) = topElev(ft)*12 + |botElev(ft)|*12 - tpThick(in)
  function setLinked(field: 'height' | 'topElev' | 'botElev', value: number) {
    const topIn = (field === 'topElev' ? value : mp.topElev) * 12;
    const botIn = Math.abs((field === 'botElev' ? value : mp.botElev) * 12);
    if (field === 'height') {
      // User changed height → adjust topElev, others stay
      const newTopIn = value - botIn + tpThick;
      updateMpVar(panel!.id, { height: value, topElev: newTopIn / 12 });
    } else {
      // topElev or botElev changed → recalculate height
      const newHeight = topIn + botIn - tpThick;
      updateMpVar(panel!.id, { [field]: value, height: newHeight } as Partial<MpVar>);
    }
  }

  function setTpThick(value: number) {
    const topIn = mp.topElev * 12;
    const botIn = Math.abs(mp.botElev * 12);
    const newHeight = topIn + botIn - value;
    updatePanel(panel!.id, { tpvar: { ...tp, thickness: value } });
    updateMpVar(panel!.id, { height: newHeight });
  }

  const isMetric = ui.units === 'metric';

  function dimField(label: string, key: keyof MpVar, unit = '"', min?: number) {
    const val = mp[key] as number;
    // Map mpvar keys to the warning IDs that indicate that field is out of range
    const FIELD_WARN_IDS: Partial<Record<keyof MpVar, string[]>> = {
      thickness: ['geom-thickness-min', 'geom-thickness-low'],
      width:     ['geom-width-short-panel', 'geom-width-tall-panel'],
      height:    ['geom-height-max', 'geom-slenderness'],
    };
    const fieldWarnIds = FIELD_WARN_IDS[key] ?? [];
    const hasWarning   = fieldWarnIds.some(id => geomWarnIds.has(id));
    return (
      <div className={`props-row${hasWarning ? ' has-warning' : ''}`} key={key}>
        <label className="props-label">{label}</label>
        <FeetInchesInput
          ref={el => { dimRefs.current[key] = el; }}
          value={val}
          onChange={v => set(key, v as MpVar[typeof key])}
          unit={unit}
          metric={isMetric}
          min={min}
        />
      </div>
    );
  }

  function toggleSection(key: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function renderEntityGroup(g: EntityGroup) {
    const slots = (panel as unknown as Record<string, unknown>)[g.key] as Slot<Record<string, unknown>>[];
    if (!slots) return null;
    const enabledSlots = slots
      .map((s, i) => ({ slot: s, index: i }))
      .filter(({ slot }) => slot.enabled);
    const isOpen = expanded.has(g.key);

    const sectionId = `props-${g.key}-section`;
    return (
      <div key={g.key} className="props-entity-section">
        <div className="props-entity-header">
          <button
            type="button"
            className="props-entity-toggle"
            aria-expanded={isOpen}
            aria-controls={sectionId}
            onClick={() => toggleSection(g.key)}
          >
            <span className="props-entity-chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
            <span className="props-entity-name">{g.label}</span>
            <span className="props-entity-count">{enabledSlots.length}</span>
          </button>
          <button
            type="button"
            className="props-entity-dialog-btn"
            onClick={() => setActiveDialog(g.dialog)}
            title={`Open ${g.label} dialog`}
            aria-label={`Open ${g.label} dialog`}
          >
            ⛭
          </button>
        </div>
        {isOpen && enabledSlots.length > 0 && (
          <div id={sectionId} className="props-entity-slots">
            {enabledSlots.map(({ slot, index }) => {
              const entityId = `${g.key}-${index}`;
              const isSelected = selSet.has(entityId);
              return (
                <div
                  key={entityId}
                  className={`props-slot-row${isSelected ? ' selected' : ''}`}
                  onClick={() => selectEntity(entityId)}
                  title={`Select ${entityId}`}
                >
                  <span className="props-slot-mark">{g.markFn(slot.data as Record<string, unknown>, index)}</span>
                  <span className="props-slot-index">#{index}</span>
                </div>
              );
            })}
          </div>
        )}
        {isOpen && enabledSlots.length === 0 && (
          <div id={sectionId} className="props-entity-empty">None</div>
        )}
      </div>
    );
  }

  // Panel body row (always present)
  const panelBodySelected = selSet.has('panel-body');

  return (
    <aside className={`props-panel${ui.propsOpen ? '' : ' collapsed'}`} style={style}>
      {!ui.propsOpen ? (
        <button className="props-expand-btn" onClick={toggleProps} title="Show Properties">&#x25C4;</button>
      ) : (
      <>
      <div className="props-header">
        <span className="props-title">PROPERTIES</span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>

          <button
            className="props-units-btn"
            onClick={toggleUnits}
            title={isMetric ? 'Switch to Imperial (ft-in)' : 'Switch to Metric (mm)'}
          >
            {isMetric ? 'mm' : 'ft'}
          </button>
          <button
            className="props-edit-btn"

            onClick={() => setActiveDialog('panel-dims')}
            title="Panel Settings"
          >
            ⛭
          </button>
          <button
            className="props-collapse-btn"
            onClick={toggleProps}
            title="Collapse Properties"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="props-body">
        {/* Panel body entity + inline mark edit */}
        <div
          className={`props-slot-row panel-body-row${panelBodySelected ? ' selected' : ''}`}
          onClick={() => selectEntity('panel-body')}
          title="Select panel body"
        >
          <input
            className="props-input"
            type="text"
            value={mp.panelMark}
            onChange={e => set('panelMark', e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{ flex: 1, fontWeight: 600, fontSize: 13 }}
          />
          <span className="props-slot-index">body</span>
        </div>

        {/* Panel geometry — collapsible accordion */}
        <div className="props-entity-section">
          <div className="props-entity-header">
            <button
              type="button"
              className="props-entity-toggle"
              aria-expanded={expanded.has('geometry')}
              aria-controls="props-geometry-section"
              onClick={() => toggleSection('geometry')}
            >
              <span className="props-entity-chevron" aria-hidden="true">{expanded.has('geometry') ? '▾' : '▸'}</span>
              <span className="props-entity-name">Geometry</span>
            </button>
          </div>
          {expanded.has('geometry') && (
          <div id="props-geometry-section" className="props-group">
            {dimField('Width',     'width')}
            <div className="props-row" key="height">
              <label className="props-label">Height</label>
              <FeetInchesInput
                ref={el => { dimRefs.current['height'] = el; }}
                value={mp.height}
                onChange={v => setLinked('height', v)}
                unit='"'
                metric={isMetric}
              />
            </div>
            {dimField('Thickness', 'thickness')}
            <div className="props-row" key="topElev">
              <label className="props-label">Top Elev</label>
              <FeetInchesInput
                ref={el => { dimRefs.current['topElev'] = el; }}
                value={mp.topElev * 12}
                onChange={v => setLinked('topElev', v / 12)}
                unit="'"
                metric={isMetric}
              />
            </div>
            <div className="props-row" key="botElev">
              <label className="props-label">Bot Elev</label>
              <FeetInchesInput
                ref={el => { dimRefs.current['botElev'] = el; }}
                value={mp.botElev * 12}
                onChange={v => setLinked('botElev', v / 12)}
                unit="'"
                metric={isMetric}
                min={-Infinity}
              />
            </div>
            <div className="props-row" key="tpThick">
              <label className="props-label">Top Plate</label>
              <FeetInchesInput
                ref={el => { dimRefs.current['tpThick'] = el; }}
                value={tpThick}
                onChange={v => setTpThick(v)}
                unit='"'
                metric={isMetric}
              />
            </div>
            <div className="props-row" key="toc-derived">
              <label className="props-label" style={{ color: '#888' }}>TOC</label>
              <span className="props-input" style={{ background: '#f5f5f5', border: 'none', fontSize: 12, padding: '4px 8px' }}>
                {formatImperial((mp.topElev ?? 0) * 12 - tpThick)}
              </span>
            </div>
            {dimField('Roof Line L', 'roofLine')}
            {mp.roofLineSloped && dimField('Roof Line R', 'roofLineR')}
            <div className="props-row" key="roof-slope-toggle">
              <label className="props-label">Sloped</label>
              <input
                type="checkbox"
                checked={!!mp.roofLineSloped}
                onChange={e => {
                  set('roofLineSloped', e.target.checked);
                  if (e.target.checked && mp.roofLineR == null) {
                    set('roofLineR', mp.roofLine ?? 0);
                  }
                }}
              />
            </div>
            {/* ── Panel top geometry (corner heights + angles) ─────────── */}
            <div className="props-row" key="heightL-row">
              <label className="props-label">Height L</label>
              <FeetInchesInput
                value={mp.heightL ?? mp.height}
                onChange={v => set('heightL', v as MpVar['heightL'])}
                unit='"'
                metric={isMetric}
              />
            </div>
            <div className="props-row" key="heightR-row">
              <label className="props-label">Height R</label>
              <FeetInchesInput
                value={mp.heightR ?? mp.heightL ?? mp.height}
                onChange={v => set('heightR', v as MpVar['heightR'])}
                unit='"'
                metric={isMetric}
              />
            </div>
            {(() => {
              const hL = mp.heightL ?? mp.height;
              const hR = mp.heightR ?? hL;
              const W = mp.width;
              const isAutoL = mp.topAngleLocked || mp.topAngleL === 'auto';
              const isAutoR = mp.topAngleLocked || mp.topAngleR === 'auto';
              const bothAuto = isAutoL && isAutoR;
              const computedL = bothAuto ? autoAngle(hL, hR, W) : 0;
              const computedR = bothAuto ? autoAngle(hR, hL, W) : 0;
              const displayL = isAutoL ? computedL.toFixed(1) : String(mp.topAngleL);
              const displayR = isAutoR ? computedR.toFixed(1) : String(mp.topAngleR);
              return (<>
            <div className="props-row" key="top-angle-l">
              <label className="props-label">Angle L</label>
              <input
                className="props-input"
                type="text"
                value={displayL}
                onChange={e => {
                  const v = parseAngleInput(e.target.value);
                  set('topAngleL', v as MpVar['topAngleL']);
                }}
                disabled={mp.topAngleLocked}
                placeholder="Auto"
                title="Degrees or pitch (e.g. 4/12). Auto = direct to other corner."
                style={{ fontSize: 12, padding: '3px 6px', width: 70, opacity: mp.topAngleLocked ? 0.5 : 1 }}
              />
              <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>°</span>
            </div>
            <div className="props-row" key="top-angle-r">
              <label className="props-label">Angle R</label>
              <input
                className="props-input"
                type="text"
                value={displayR}
                onChange={e => {
                  const v = parseAngleInput(e.target.value);
                  set('topAngleR', v as MpVar['topAngleR']);
                }}
                disabled={mp.topAngleLocked}
                placeholder="Auto"
                title="Degrees or pitch (e.g. 4/12). Auto = direct to other corner."
                style={{ fontSize: 12, padding: '3px 6px', width: 70, opacity: mp.topAngleLocked ? 0.5 : 1 }}
              />
              <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>°</span>
            </div>
              </>);
            })()}
            <div className="props-row" key="angle-lock">
              <label className="props-label">Auto</label>
              <input
                type="checkbox"
                checked={mp.topAngleLocked}
                onChange={e => {
                  set('topAngleLocked', e.target.checked);
                  if (e.target.checked) {
                    set('topAngleL', 'auto' as MpVar['topAngleL']);
                    set('topAngleR', 'auto' as MpVar['topAngleR']);
                  }
                }}
              />
            </div>
            <div className="props-row" key="top-radius-l">
              <label className="props-label">Radius L</label>
              <FeetInchesInput
                value={mp.topRadiusL ?? 0}
                onChange={v => set('topRadiusL', v as MpVar['topRadiusL'])}
                unit='"'
                metric={isMetric}
              />
            </div>
            <div className="props-row" key="top-radius-r">
              <label className="props-label">Radius R</label>
              <FeetInchesInput
                value={mp.topRadiusR ?? 0}
                onChange={v => set('topRadiusR', v as MpVar['topRadiusR'])}
                unit='"'
                metric={isMetric}
              />
            </div>
            {dimField('Slab Thick','slabThickness')}
            <div className="props-row" key="casting-orientation">
              <label className="props-label">Casting</label>
              <select
                className="props-input"
                value={mp.castingOrientation ?? 'face-down'}
                onChange={e => set('castingOrientation', e.target.value as 'face-down' | 'face-up')}
                style={{ fontSize: 12, padding: '3px 6px' }}
              >
                <option value="face-down">Face Down (OUT)</option>
                <option value="face-up">Face Up (IN)</option>
              </select>
            </div>
            <div className="props-row" key="drawing-scale">
              <label className="props-label">Scale</label>
              <input
                className="props-input"
                type="text"
                value={mp.drawingScale ?? ''}
                onChange={e => set('drawingScale', e.target.value)}
                placeholder="Auto"
                style={{ fontSize: 12, padding: '3px 6px' }}
              />
            </div>
          </div>
          )}
        </div>

        {/* Calculated properties — derived from panel geometry + openings */}
        <div className="props-entity-section">
          <div className="props-entity-header">
            <button
              type="button"
              className="props-entity-toggle"
              aria-expanded={expanded.has('calculated')}
              aria-controls="props-calculated-section"
              onClick={() => toggleSection('calculated')}
            >
              <span className="props-entity-chevron" aria-hidden="true">{expanded.has('calculated') ? '▾' : '▸'}</span>
              <span className="props-entity-name">Calculated</span>
            </button>
          </div>
          {expanded.has('calculated') && (() => {
            const mat = calcMaterials(panel);
            const derivedStyle: React.CSSProperties = { background: '#f5f5f5', border: 'none', fontSize: 12, padding: '4px 8px' };
            const fmtDim = (v: number) => isMetric ? formatMetric(v) : formatImperial(v);
            const fmtArea = (sqFt: number, sqM: number) => isMetric ? `${sqM.toFixed(2)} m²` : `${sqFt.toFixed(1)} sq ft`;
            const fmtVol  = (cuYd: number, cuM: number) => isMetric ? `${cuM.toFixed(3)} m³` : `${cuYd.toFixed(3)} yd³`;
            const fmtWt   = (kips: number, kg: number) => isMetric ? `${Math.round(kg).toLocaleString()} kg` : `${kips.toFixed(1)} kips (${Math.round(mat.panelWeightLb).toLocaleString()} lb)`;
            const fmtLoad = (lb: number) => {
              if (lb === 0) return '—';
              return isMetric ? `${Math.round(lb * 0.453592).toLocaleString()} kg` : `${(lb / 1000).toFixed(1)} kips`;
            };
            return (
            <div id="props-calculated-section" className="props-group">
              <div className="props-row" key="cg-x">
                <label className="props-label" title="Center of gravity X from left edge">CG (X)</label>
                <span className="props-input" style={derivedStyle}>{fmtDim(mat.cgX)}</span>
              </div>
              <div className="props-row" key="cg-y">
                <label className="props-label" title="Center of gravity Y from bottom edge">CG (Y)</label>
                <span className="props-input" style={derivedStyle}>{fmtDim(mat.cgY)}</span>
              </div>
              <div className="props-row" key="cg-note">
                <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic', gridColumn: '1 / -1' }}>Does not factor in weight offset from rebar.</span>
              </div>
              <div className="props-row" key="volume">
                <label className="props-label">Volume</label>
                <span className="props-input" style={derivedStyle}>{fmtVol(mat.concreteCuYd, mat.concreteCuM)}</span>
              </div>
              <div className="props-row" key="weight">
                <label className="props-label">Weight</label>
                <span className="props-input" style={derivedStyle}>{fmtWt(mat.panelWeightKips, mat.panelWeightKg)}</span>
              </div>
              <div className="props-row" key="ext-face">
                <label className="props-label" title="Gross exterior face area (includes openings)">Ext. Face Area</label>
                <span className="props-input" style={derivedStyle}>{fmtArea(mat.exteriorFaceAreaSqFt, mat.formAreaSqM)}</span>
              </div>
              <div className="props-row" key="finished">
                <label className="props-label" title="Exterior face minus openings and feature recesses">Finished Area</label>
                <span className="props-input" style={derivedStyle}>{fmtArea(mat.finishedAreaSqFt, mat.finishedAreaSqM)}</span>
              </div>
              <div className="props-row" key="slenderness">
                <label className="props-label" title="Height / Thickness ratio">Slenderness (H/T)</label>
                <span className="props-input" style={derivedStyle}>{mat.slendernessRatio.toFixed(1)}</span>
              </div>
              <div className="props-row" key="load-pick">
                <label className="props-label" title="Panel weight ÷ pick points">Load / Pick</label>
                <span className="props-input" style={derivedStyle}>{mat.pickCount > 0 ? fmtLoad(mat.craneLoadPerPickLb) : `— (${mat.pickCount} picks)`}</span>
              </div>
              <div className="props-row" key="load-brace">
                <label className="props-label" title="Panel weight ÷ brace points">Load / Brace</label>
                <span className="props-input" style={derivedStyle}>{mat.braceCount > 0 ? fmtLoad(mat.braceLoadPerPointLb) : `— (${mat.braceCount} braces)`}</span>
              </div>
              <div className="props-row" key="form-area">
                <label className="props-label" title="Gross face area for form oil/sealer estimation">Form Area</label>
                <span className="props-input" style={derivedStyle}>{fmtArea(mat.formAreaSqFt, mat.formAreaSqM)}</span>
              </div>
            </div>
            );
          })()}
        </div>

        <div className="props-divider" />

        {/* Entity group sections */}
        {ENTITY_CATEGORIES.map(cat => (
          <div key={cat.label} className="props-category">
            <div className="props-category-label">{cat.label}</div>
            {cat.groups.map(g => renderEntityGroup(g))}
          </div>
        ))}

        <div className="props-divider" />

        {/* Panel tools — dialog launchers for non-entity panel features */}
        <div className="props-category">
          <div className="props-category-label">Panel Tools</div>
          {[
            { label: 'Program Options',    dialog: 'panel-options' as ActiveDialog },
            { label: 'Ledgers',             dialog: 'ledgers' as ActiveDialog },
            { label: 'Change Thickness',    dialog: 'change-thickness' as ActiveDialog },
            { label: 'Panel Attributes',    dialog: 'panel-attributes' as ActiveDialog },
          ].map(t => (
            <div key={t.dialog} className="props-entity-section">
              <div className="props-entity-header">
                <button
                  type="button"
                  className="props-entity-toggle"
                  onClick={() => setActiveDialog(t.dialog)}
                  title={`Open ${t.label} dialog`}
                  aria-label={`Open ${t.label} dialog`}
                >
                  <span className="props-entity-name">{t.label}</span>
                  <span className="props-entity-dialog-btn" aria-hidden="true">⛭</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </aside>
  );
}
